import { useEffect, useRef, useState } from "react";
// REDUX
import { useLazyGetBankQuery } from "../../redux/banks/bankAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";

// Resolves a map of bankId -> adapter protocol_type (lowercased) for the
// given banks. The monitor-view layout payload does not include the full
// adapter object, so the authoritative /banks/:id endpoint is used. This
// reuses the same RTK Query cache as MonitoringBank, so it typically does
// not incur extra network requests.
export default function useGetBankProtocols(bankIds: string[]) {
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [protocolByBank, setProtocolByBank] = useState<Map<string, string>>(
    new Map()
  );
  const [token, setToken] = useState<string | null>(null);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetBank] = useLazyGetBankQuery();

  // Track previous bankIds to avoid redundant fetches
  const prevBankIdsRef = useRef<string>("");

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  useEffect(() => {
    if (!token || bankIds.length === 0) return;

    const fingerprint = bankIds.slice().sort().join(",");
    if (fingerprint === prevBankIdsRef.current) return;
    prevBankIdsRef.current = fingerprint;

    const fetchAll = async () => {
      const next = new Map<string, string>();

      await Promise.all(
        bankIds.map(async (bankId) => {
          try {
            const resp = await triggerGetBank({ token, bankId }, true).unwrap();
            const protocol = (resp?.data?.adapter?.protocol_type ?? "")
              .toString()
              .toLowerCase();
            next.set(bankId, protocol);
          } catch (e) {
            next.set(bankId, "");
          }
        })
      );

      setProtocolByBank(next);
    };

    fetchAll();
  }, [token, bankIds, triggerGetBank]);

  // ************************************************ */
  // RETURN **************************************** */
  return { protocolByBank };
}
